import React, { useState, useEffect } from "react";
import { TIER_LABELS } from "@/lib/tiers";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { api, uploadImage } from "@/lib/api";
import { Button, Input, Textarea } from "@/components/primitives";
import { GoogleButton } from "@/components/BrandLogos";
import { X } from "lucide-react";

const emptyRef = () => ({ name: "", relationship: "", contact: "" });

const PLANS = [
  { id: "free", name: TIER_LABELS.free, price: "$0", desc: "Connect, attend events, join groups and the community." },
  { id: "individual", name: TIER_LABELS.plus, price: "$7/mo", desc: "Connect, attend events, unlock real introductions and opportunities." },
  { id: "business", name: TIER_LABELS.business, price: "$37/mo", desc: "Host events and post jobs as your organization." },
  { id: "brand_partner", name: TIER_LABELS.brand_partner, price: "Custom", desc: "Partner with Two Degrees, reviewed & approved by our team." },
];

export default function Register() {
  const { register, googleLogin } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [refCode, setRefCode] = useState("");
  const [refBannerDismissed, setRefBannerDismissed] = useState(() => sessionStorage.getItem("ref_banner_dismissed") === "1");
  useEffect(() => {
    const r = (searchParams.get("ref") || "").trim();
    if (r) { setRefCode(r); try { localStorage.setItem("td_ref", r); } catch { /* ignore */ } }
    else { try { setRefCode(localStorage.getItem("td_ref") || ""); } catch { /* ignore */ } }
  }, [searchParams]);
  const [f, setF] = useState({ fullName: "", email: "", password: "", joinReason: "" });
  useEffect(() => {
    const em = (searchParams.get("email") || "").trim();
    if (em) setF((prev) => (prev.email ? prev : { ...prev, email: em }));
  }, [searchParams]);
  const [wantsVerification, setWantsVerification] = useState(false);
  const [refs, setRefs] = useState([emptyRef(), emptyRef()]);
  const [workLink, setWorkLink] = useState("");
  const [workSamples, setWorkSamples] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [plan, setPlan] = useState("free");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setRef = (i, k) => (e) => setRefs(refs.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));

  const uploadWork = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setWorkSamples((s) => [...s, url]);
      toast.success("Work sample added");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!agreed) return toast.error("Please agree to the Terms of Service and Privacy Policy to continue");
    const verify = wantsVerification && plan !== "brand_partner";
    if (verify) {
      const valid = refs.filter((r) => r.name.trim() && r.contact.trim());
      if (valid.length < 2) return toast.error("Please provide 2 references (name + contact) for verification");
      if (!workLink.trim() && workSamples.length === 0) return toast.error("Add a work link or upload a sample to be verified");
    }
    setLoading(true);
    try {
      const utm = {
        utm_source: searchParams.get("utm_source") || undefined,
        utm_medium: searchParams.get("utm_medium") || undefined,
        utm_campaign: searchParams.get("utm_campaign") || undefined,
        signup_source: searchParams.get("utm_source") || searchParams.get("ref_source") || undefined,
        referrer: (typeof document !== "undefined" && document.referrer) || undefined,
      };
      await register(f.email.trim(), f.password, f.fullName.trim(), {
        join_reason: f.joinReason,
        agreed_terms: true,
        third_party_ack: true,
        plan_choice: plan,
        referral_code: refCode || undefined,
        wants_verification: verify,
        references: verify ? refs.filter((r) => r.name.trim()) : [],
        work_link: verify ? workLink.trim() : "",
        work_samples: verify ? workSamples : [],
        ...utm,
      });
      try { localStorage.removeItem("td_ref"); } catch { /* ignore */ }
      nav("/");
    } catch (err) {
      const d = err?.data?.detail;
      if (d?.code === "email_exists") {
        toast.info(d.message || "You already have an account. Please sign in.");
        nav("/login", { state: { email: f.email.trim() } });
      } else {
        toast.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-6">
      <div className="w-full max-w-md py-8">
        <img src="/brand/wordmark-color.png" alt="Two Degrees" className="h-12 w-auto object-contain mb-5" data-testid="register-brand-logo" />
        <h1 className="font-serif text-4xl text-ink mb-1">Join Two Degrees</h1>
        <p className="text-muted-foreground font-sans mb-8">Build the network that opens doors.</p>

        {refCode && !refBannerDismissed && (
          <div data-testid="register-referral-banner" className="mb-6 rounded-xl border border-brand/30 bg-brand-tint px-4 py-3 flex items-center gap-3">
            <p className="flex-1 text-sm text-brand-tint-ink">You were invited by a member. You&apos;re joining with a referral.</p>
            <button data-testid="register-referral-dismiss" aria-label="Dismiss"
              onClick={() => { sessionStorage.setItem("ref_banner_dismissed", "1"); setRefBannerDismissed(true); }}
              className="shrink-0 -mr-1 p-1 rounded-full text-brand-tint-ink/60 hover:text-brand-tint-ink hover:bg-brand/10 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-4">
          <Input label="Full name" data-testid="register-name" value={f.fullName} onChange={set("fullName")} placeholder="Ada Lovelace" required />
          <Input label="Email" type="email" data-testid="register-email" value={f.email} onChange={set("email")} placeholder="you@work.com" required />
          <Input label="Password" type="password" data-testid="register-password" value={f.password} onChange={set("password")} placeholder="At least 6 characters" required />
          <Textarea label="What brings you here?" data-testid="register-reason" value={f.joinReason} onChange={set("joinReason")} placeholder="What are you hoping to get out of the community?" />

          {/* Plan selection */}
          <div className="flex flex-col gap-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Choose your membership</p>
            {PLANS.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`register-plan-${p.id}`}
                onClick={() => setPlan(p.id)}
                className={`flex items-start justify-between gap-3 text-left rounded-lg border p-3 ${plan === p.id ? "border-brand bg-brand-tint" : "border-line bg-surface-2 hover:bg-surface-3"}`}
              >
                <span className="min-w-0">
                  <span className="block text-ink text-sm font-medium">{p.name}</span>
                  <span className="block text-muted-foreground text-xs mt-0.5">{p.desc}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-ink text-sm font-serif">{p.price}</span>
                  <span className={`inline-block w-4 h-4 rounded-full border mt-1 ${plan === p.id ? "border-brand bg-brand" : "border-line"}`} />
                </span>
              </button>
            ))}
            {plan !== "free" && (
              <p className="text-brand-2 text-xs" data-testid="register-plan-note">
                {plan === "brand_partner"
                  ? "Brand Partner memberships are reviewed and approved by our team, you'll join as a member first."
                  : "You'll join on the free tier and can activate billing for this plan from your account after signing up."}
              </p>
            )}
          </div>
          {/* Verification opt-in (not applicable to Brand Partner) */}
          {plan !== "brand_partner" && (
          <div className="bg-surface-2 border border-line rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="register-verify-optin" checked={wantsVerification} onChange={(e) => setWantsVerification(e.target.checked)} className="mt-1 w-4 h-4 accent-brand" />
              <span>
                <span className="text-ink text-sm font-medium">I&apos;d like to be verified</span>
                <span className="block text-muted-foreground text-xs mt-0.5">Get the verified badge (added by our team after review). Requires 2 references and proof of your work.</span>
              </span>
            </label>

            {wantsVerification && (
              <div className="mt-4 flex flex-col gap-4">
                {refs.map((r, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Reference {i + 1}</p>
                    <Input data-testid={`register-ref-${i}-name`} value={r.name} onChange={setRef(i, "name")} placeholder="Name" className="py-2 text-sm" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid={`register-ref-${i}-rel`} value={r.relationship} onChange={setRef(i, "relationship")} placeholder="Relationship" className="py-2 text-sm" />
                      <Input data-testid={`register-ref-${i}-contact`} value={r.contact} onChange={setRef(i, "contact")} placeholder="Email or phone" className="py-2 text-sm" />
                    </div>
                  </div>
                ))}
                <div>
                  <Input label="Link to your work" data-testid="register-work-link" value={workLink} onChange={(e) => setWorkLink(e.target.value)} placeholder="https://yoursite.com" />
                  <label className="inline-flex items-center gap-2 mt-2 bg-surface-3 hover:bg-line text-ink text-xs px-3 py-2 rounded-pill cursor-pointer transition-colors">
                    {uploading ? "Uploading…" : "Upload a work sample"}
                    <input type="file" className="hidden" onChange={uploadWork} data-testid="register-work-upload" disabled={uploading} />
                  </label>
                  {workSamples.length > 0 && <p className="text-brand-2 text-xs mt-1">{workSamples.length} sample(s) attached</p>}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Terms, Privacy, IP + third-party acknowledgment consent */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" data-testid="register-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 w-4 h-4 accent-brand shrink-0" />
            <span className="text-ink text-xs leading-relaxed">
              I have read and agree to the Two Degrees{" "}
              <Link to="/legal?doc=terms" data-testid="register-agreement-link" target="_blank" className="text-brand hover:underline">Terms of Service</Link>{" "}
              and the Two Degrees{" "}
              <Link to="/legal?doc=terms" data-testid="register-ip-link" target="_blank" className="text-brand hover:underline">Intellectual Property and Proprietary Rights Policy</Link>{" "}
              and{" "}
              <Link to="/legal?doc=privacy" data-testid="register-privacy-link" target="_blank" className="text-brand hover:underline">Privacy Policy</Link>
              {", and acknowledge that Two Degrees uses the following third-party services subject to their own terms: "}
              <a href="https://stripe.com/legal" target="_blank" rel="noreferrer" className="text-brand hover:underline">Stripe</a>{" (stripe.com/legal), "}
              <a href="https://stripe.com/legal/treasury" target="_blank" rel="noreferrer" className="text-brand hover:underline">Stripe Treasury</a>{" (stripe.com/legal/treasury), "}
              <a href="https://plaid.com/legal/end-user-privacy-policy" target="_blank" rel="noreferrer" className="text-brand hover:underline">Plaid</a>{" (plaid.com/legal), "}
              <a href="https://gusto.com/about/terms" target="_blank" rel="noreferrer" className="text-brand hover:underline">Gusto</a>{" (gusto.com/about/terms), "}
              <a href="https://www.microsoft.com/servicesagreement" target="_blank" rel="noreferrer" className="text-brand hover:underline">Microsoft Teams</a>{" (microsoft.com/servicesagreement), HubSpace CRM, "}
              <a href="https://www.tiktok.com/legal/terms-of-service" target="_blank" rel="noreferrer" className="text-brand hover:underline">TikTok</a>{" (tiktok.com/legal/terms-of-service), "}
              <a href="https://www.facebook.com/terms" target="_blank" rel="noreferrer" className="text-brand hover:underline">Meta/Facebook/Instagram</a>{" (facebook.com/terms), "}
              <a href="https://www.linkedin.com/legal/user-agreement" target="_blank" rel="noreferrer" className="text-brand hover:underline">LinkedIn</a>{" (linkedin.com/legal/user-agreement), and "}
              <a href="https://twitter.com/en/tos" target="_blank" rel="noreferrer" className="text-brand hover:underline">X/Twitter</a>{" (twitter.com/en/tos). I further acknowledge that all platform content, branding, design, code, features, trade secrets, and proprietary information are the sole and exclusive property of Two Degrees LLC. Unauthorized use, reproduction, copying, distribution, or reverse engineering of any Two Degrees branding, code, or platform materials without prior written consent from Two Degrees LLC is strictly prohibited."}
            </span>
          </label>

          <Button type="submit" data-testid="register-submit" loading={loading} disabled={!agreed} size="lg" className="mt-2">Create account</Button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px bg-divider flex-1" />
          <span className="text-muted-foreground text-xs font-sans">or</span>
          <div className="h-px bg-divider flex-1" />
        </div>

        <GoogleButton text="Continue with Google" onClick={googleLogin} testID="register-google" />

        <p className="text-center text-muted-foreground text-sm font-sans mt-8">
          Already a member? <Link to="/login" data-testid="go-login" className="text-brand hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
